//Library Imports
import { Typography, message } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import store, { RootState } from 'store';
import { useSelector } from 'react-redux';
import React from 'react';

//Local Imports
import TableSort from 'modules/Common/Components/TableSort';
import { getContentContactsList, resetContentContactsList, resetContentContactsOnly } from '../Contacts/action';
import Speed from '../../../assets/img/icons/Speed.svg';
import { ANALYTICS_INTENT_DATA, CONTACT_ANALYTICS } from 'constants/types';
import { ANALYTICS_DEFAULT_DATES_FILTER, RedirectToContactDetails } from '../Contacts/utils';
import { getConstantCampaignEmailId, setSortDirection } from 'modules/Analytics/utils';
import ContactLoader from '../Contacts/Loaders/ContactLoader';
import BlankProfilePhoto from 'assets/img/blank_profile_photo.png';

//CSS Imports
import styled from 'styled-components';
import { addCommaToNumber, getQuarterDates } from 'utils';
import ContentLoader from 'react-content-loader';
import SearchField from '../Shared/SearchField';
import { getAnalyticsFilterId, getQueryArticleId, getQueryArticleLink } from 'modules/EmailBrowser/utils';
import { useHistory } from 'react-router-dom';
import { analyticsDatesFilterChange } from '../Dashboard/actions';
import { getDeliveredContactsListService } from 'modules/Services/AnalyticsService';
import ExportAnalyticsPopup from '../Shared/ExportAnalyticsPopup';
import { disabledLinks } from './utils';
import ConfirmDialog from 'modules/Common/Components/ConfirmDialog';

const ContentContact = () => {
  //Local States
  const queryArticleId = getQueryArticleId();
  const queryArticleLink = getQueryArticleLink();
  const fsId = getAnalyticsFilterId();

  const [appliedFiltersLoading, setAppliedFiltersLoading] = useState(true);
  const [firstRender, setFirstRender] = useState(true);
  const [searchedText, setSearchedText] = useState('');
  const [sortType, setSortType] = useState<{ accessor: string; sortDirection: string }>({ accessor: '', sortDirection: 'none' });
  const [sortBy, setSortBy] = useState('');
  const [newListBtnDisable, setNewListBtnDisable] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  //Redux states
  const appliedCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignIds);
  const appliedWorkflowsIds = useSelector((state: RootState) => state.analyticsDashboard.appliedWorkflowsIds);
  const appliedAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.appliedAssetsIds);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights, isAllCampaigns } = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters,
  );
  const isAllDailyWeeklyShareFlag = isAllCampaigns || isShareProfile || isDailyIQ || isWeeklyAccountInsights;
  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);
  const { contentContacts, contentContactsLoading, totalContentContacts, totalArticleScore, articleUrl } = useSelector(
    (state: RootState) => state.analyticsContent,
  );
  const userName = useSelector((state: RootState) => `${state.user.userData.first_name} ${state.user.userData.last_name}`);
  let { startDate, endDate, appliedDatesObject } = store.getState().analyticsGraph;
  const disableExportButton = useSelector((state: RootState) => state.analyticsDashboard.exportLoading);

  const { Text } = Typography;
  const [ellipsis, setEllipsis] = React.useState(true);
  const history = useHistory();

  const confirmationDialog = {
    cancel: 'No',
    confirm: 'Yes',
    title: 'Are you sure you want to create a list?',
    className: 'confirm-dialog-content-contact',
    onConfirm: () => {
      // based on design ready we need to redirect to new list
      createNewList();
    },
    onCancel: () => {
      setShowConfirmationDialog(false);
    },
  };

  const getParams = (
    offset: number,
    resetSearch?: boolean,
    isExport?: boolean,
    createNewList?: boolean,
    sort?: { accessor: ''; sortDirection: '' },
  ) => {
    let analyticsDashboard = store.getState().analyticsDashboard;
    let { appliedCampaignIds, appliedAssetsIds, appliedWorkflowsIds, excludeCampaignsIds, excludeAssetsIds } = analyticsDashboard;

    let campaignIds = [];
    let emailIds = [];
    let workflowIds = [];

    if (!(fsId && firstRender)) {
      campaignIds = appliedCampaignIds;
      emailIds = appliedAssetsIds;
      workflowIds = appliedWorkflowsIds;
      if (startDate === '' && endDate === '') {
        analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
        startDate = getQuarterDates().start_date;
        endDate = getQuarterDates().end_date;
      }
      // check if is share profile || daily IQ || weekly accounts insights
      if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
        let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
        campaignIds = [campaign_email_id];
        emailIds = [campaign_email_id];
      }
    }

    let _params: any = {
      allCampaigns: AllCampaignsFilterCheck,
      limit: 20,
      searchWord: resetSearch ? '' : searchedText,
      offset: offset,
      emailIds: emailIds,
      workflowIds,
      startDate: startDate,
      endDate: endDate,
      campaignIds: campaignIds,
      articleId: queryArticleId,
      articleLink: queryArticleLink,
      exportAnalytics: isExport,
      createNewList: createNewList,
      listName: createNewList ? `${userName} New List ${new Date().toUTCString().replace(',', ' ')}` : '',
      sortBy: sort !== undefined ? sort?.accessor : sortBy,
      sortType: sort !== undefined ? sort?.sortDirection : sortType.sortDirection,
    };

    if (AllCampaignsFilterCheck) {
      _params['excludeCampaignsIds'] = excludeCampaignsIds;
      _params['excludeAssetsIds'] = excludeAssetsIds;
    }
    return _params;
  };

  const clearSearch = () => {
    setSearchedText('');
    resetContentContactsList();
    getContentContactsList(getParams(0, true));
  };

  const handleSearchContacts = () => {
    if (searchedText.trim()) {
      resetContentContactsList();
      getContentContactsList(getParams(0));
    }
  };

  const exportContentContacts = () => {
    store.dispatch({
      type: ANALYTICS_INTENT_DATA.EXPORT_LOAD,
      payload: true,
    });
    getContentContactsList(getParams(0, false, true));
  };

  const createNewList = () => {
    setNewListBtnDisable(true);
    getDeliveredContactsListService(getParams(0, false, false, true)).then((data: any) => {
      setNewListBtnDisable(false);
      if (data.data.success === true) {
        history.push(`/audiences/view/${window.location.search}&list_id=${data.data.list_id}`);
        message.success({
          content: `List has been created successfully`,
        });
      }
    });
  };

  useEffect(() => {
    if (appliedCampaignsIds.length > 0 || appliedWorkflowsIds.length > 0 || isAllDailyWeeklyShareFlag) {
      setAppliedFiltersLoading(false);
    }
  }, [appliedCampaignsIds, appliedWorkflowsIds, isAllDailyWeeklyShareFlag]);

  useEffect(() => {
    if (firstRender && !contentContacts.length) {
      getContentContactsList(getParams(0));
      setFirstRender(false);
    }
  }, [firstRender]);

  useEffect(() => {
    if (!firstRender) {
      if (searchedText.trim()) {
        handleSearchContacts();
      } else {
        clearSearch();
      }
    }
  }, [searchedText]);

  useEffect(() => {
    if (startDate && endDate) {
      setFilterStartDate(new Date(startDate).toLocaleString('en-us', { month: 'short', day: '2-digit', year: 'numeric' }));
      setFilterEndDate(new Date(endDate).toLocaleString('en-us', { month: 'short', day: '2-digit', year: 'numeric' }));
    }
  }, [startDate, endDate]);

  let columnsData = useMemo(
    () => [
      {
        id: 'main',
        hideHeader: true,
        disableSortBy: true,
        columns: [
          {
            id: 'contact_name',
            Header: 'Contact',
            accessor: 'contact_name',
            canSort: true,
            sortType: 'basic',
            sortDirection: sortType.accessor === 'contact_name' ? sortType.sortDirection : 'none',
            Cell: (props: any) => {
              let item = props.row.original;
              let name =
                item?.contact_name === null || (item.contact_email !== null && item.contact_name?.trim() === '')
                  ? item.contact_email
                  : item.contact_name;

              return (
                <div>
                  <div className="preview">
                    <img
                      style={{
                        boxShadow: item.personality.color ? `0px 0px 0px 2px ${item.personality.color}` : '',
                      }}
                      src={item.contact_image !== null && item.contact_image !== '' ? item.contact_image : BlankProfilePhoto}
                      alt=""
                      onError={(e: any) => {
                        e.target.onerror = null;
                      }}
                    />
                  </div>
                  <div className="details">
                    <>
                      <div className="name">
                        <Text ellipsis={ellipsis ? { tooltip: name } : false}>{name}</Text>
                      </div>
                      <div className="company">
                        <Text ellipsis={ellipsis ? { tooltip: item.contact_email } : false}>{item.contact_email}</Text>
                      </div>
                      <div className="designation">
                        <Text ellipsis={ellipsis ? { tooltip: item.job_title } : false}>{item.job_title}</Text>
                      </div>
                    </>
                  </div>
                </div>
              );
            },
          },
          {
            id: 'company_name',
            Header: 'Company',
            accessor: 'company_name',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'company_name' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return <div className="company-name">{item.company_name}</div>;
            },
          },
          {
            id: 'personality_name',
            Header: 'Personality',
            accessor: 'personality.archetype',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'personality_name' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <span className="organization d-flex align-items-center">
                  <Circle color={item.personality.color ? item.personality.color : 'black'} />
                  {item.personality.archetype === undefined ? 'N/A' : item.personality.archetype}
                </span>
              );
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: 'Clicks',
        columns: [
          {
            id: 'total_clicks',
            Header: 'Total',
            accessor: 'total_clicks',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'total_clicks' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.total_clicks ?? 0)?.toString() ?? '');
            },
          },
          {
            id: 'unique_clicks',
            Header: 'Unique',
            accessor: 'unique_clicks',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'unique_clicks' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.unique_clicks ?? 0)?.toString() ?? '');
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: 'Score',
        columns: [
          {
            id: 'campaign_score',
            Header: 'Campaign(s)',
            accessor: 'campaigns_score',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'campaign_score' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.campaigns_score ?? 0)?.toString() ?? '')}
                  </div>
                </div>
              );
            },
          },
          {
            id: 'lifetime_score',
            Header: 'Lifetime',
            accessor: 'lifetime_score',
            sortType: 'basic',
            sortDirection: sortType.accessor === 'lifetime_score' ? sortType.sortDirection : 'none',
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.lifetime_score ?? 0)?.toString() ?? '')}
                  </div>
                </div>
              );
            },
          },
        ],
      },
    ],
    [sortType],
  );

  const sortedResults = (column?: any) => {
    let sort_type = '';
    if (column.canSort) {
      setSortBy(column.id);
      sort_type = setSortDirection(column.id, column.sortDirection);
      setSortType({ accessor: column.id, sortDirection: sort_type });
      store.dispatch({
        type: CONTACT_ANALYTICS.SET_SORT_PROPS,
        payload: { sortBy: column.id, sortType: sort_type },
      });
      resetContentContactsOnly();
      getContentContactsList(getParams(0, false, false, false, { accessor: column.id, sortDirection: sort_type }));
    }
  };

  const disabledLink = (link: string) => {
    if (link) {
      return disabledLinks.some((item) => link.includes(item));
    }
  };

  const getAppliedDateLabel = (label: string) => {
    if (['Month', 'Day', 'Week'].includes(label)) {
      return '1 ' + label;
    }
    return label;
  };

  const selectedFilterText = () =>
    appliedDatesObject.label === 'Custom' ? ` in ${filterStartDate} - ${filterEndDate}` : ` in ${getAppliedDateLabel(appliedDatesObject.label)}`;

  return (
    <>
      <ExportAnalyticsPopup />
      {showConfirmationDialog && (
        <DialogWrapper>
          <div className="back-overlay" />
          <ConfirmDialog
            className="setting-delete-dialog"
            cancel={confirmationDialog.cancel}
            confirm={confirmationDialog.confirm}
            onConfirm={confirmationDialog.onConfirm}
            onCancel={confirmationDialog.onCancel}
            title={confirmationDialog.title}
            titleQuestion={null}
          />
        </DialogWrapper>
      )}

      <Wrapper>
        <div>
          <HeaderArea className="header-content">
            <div className="left-side">
              <h6 className="heading-l">
                {appliedFiltersLoading ? (
                  <ContentLoader backgroundColor="#e4e7e7" width={'25%'} style={{ height: '25px', borderRadius: '10px', marginTop: '5px' }}>
                    <rect x="0" y="0" rx="5" ry="5" width="100%" height="65" />
                  </ContentLoader>
                ) : (
                  (appliedCampaignsIds?.length > 0 || appliedWorkflowsIds.length > 0 || isAllDailyWeeklyShareFlag) && (
                    <Text
                      style={ellipsis ? { width: 660 } : undefined}
                      className="heading-l"
                      ellipsis={
                        ellipsis
                          ? {
                            tooltip: isShareProfile
                              ? `Showing results for Shared Profiles ${selectedFilterText()}`
                              : isDailyIQ
                                ? `Showing results for Daily IQ ${selectedFilterText()}`
                                : isWeeklyAccountInsights
                                  ? `Showing results for Weekly Account Insights ${selectedFilterText()}`
                                  : isAllCampaigns === true
                                    ? `Showing results for All Campaigns ${selectedFilterText()}`
                                    : appliedWorkflowsIds.length > 0
                                      ? `Showing results for selected workflow ${selectedFilterText()}`
                                      : `
                          Showing results for
                          ${appliedCampaignsIds?.length}
                          Campaign(s) and ${appliedAssetsIds?.length}
                          Email(s) ${appliedDatesObject.label === 'Custom'
                                        ? ` in ${filterStartDate} - ${filterEndDate}`
                                        : ` in ${getAppliedDateLabel(appliedDatesObject.label)}`
                                      }
                        `,
                          }
                          : false
                      }
                    >
                      {isShareProfile ? (
                        <>Showing results for Shared Profiles</>
                      ) : isDailyIQ ? (
                        <>Showing results for Daily IQ</>
                      ) : isWeeklyAccountInsights ? (
                        <>Showing results for Weekly Account Insights</>
                      ) : isAllCampaigns === true ? (
                        <>Showing results for All Campaigns</>
                      ) : appliedWorkflowsIds.length > 0 ? (
                        <>Showing results for selected workflow</>
                      ) : (
                        <>
                          Showing results for {appliedCampaignsIds?.length} Campaign(s) and {appliedAssetsIds?.length} Email(s)
                        </>
                      )}
                      {appliedDatesObject.label === 'Custom'
                        ? ` in ${filterStartDate} - ${filterEndDate}`
                        : ` in ${getAppliedDateLabel(appliedDatesObject.label)}`}
                    </Text>
                  )
                )}
              </h6>
            </div>
            <div className="right-side">
              <a className={`action action-one ${disabledLink(articleUrl) || !articleUrl ? 'disabled' : ''}`} href={articleUrl} target="_blank">
                View Link
              </a>
              <span
                className={`action action-two ${disableExportButton || totalContentContacts === 0 ? 'disabled' : ''}`}
                onClick={() => exportContentContacts()}
              >
                Export
                <svg xmlns="http://www.w3.org/2000/svg" width="1" height="22" viewBox="0 0 1 22" fill="none" className="separator">
                  <path d="M0.5 0.5V21.5" stroke="#DDDDDD" stroke-linecap="square" />
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 15 13" fill="none">
                  <path
                    d="M10.1893 4.08264L11.7192 5.57143L4.96845 5.57143C4.43218 5.57143 4.00631 5.98583 4.00631 6.50767C4.00631 7.02951 4.43218 7.44392 4.96845 7.44392L11.735 7.44392L10.205 8.9327C10.0158 9.11688 9.92114 9.3471 9.92114 9.59268C9.92114 9.83825 10.0158 10.0685 10.205 10.2527C10.5836 10.621 11.183 10.621 11.5615 10.2527L14.7161 7.16765C15.0946 6.79929 15.0946 6.21605 14.7161 5.84769L11.5457 2.76269C11.1672 2.39433 10.5678 2.39433 10.1893 2.76269C9.81073 3.13105 9.81073 3.71428 10.1893 4.08264Z"
                    fill="#7E7E7E"
                  />
                  <path
                    d="M5.48901 12.0638C5.48901 11.5419 5.06315 11.1275 4.52687 11.1275H1.92435L1.92435 1.87249L4.52687 1.87249C5.06315 1.87249 5.48901 1.45809 5.48901 0.936246C5.48901 0.414404 5.06315 0 4.52687 0L0.9622 0C0.425922 0 5.48363e-05 0.414404 5.48363e-05 0.936246L5.48363e-05 12.0638C5.48363e-05 12.5856 0.425922 13 0.9622 13H4.52687C5.06315 13 5.48901 12.5856 5.48901 12.0638Z"
                    fill="#7E7E7E"
                  />
                </svg>
              </span>
              <span className={`action action-three ${newListBtnDisable ? 'disabled' : ''}`} onClick={() => setShowConfirmationDialog(true)}>
                Create new List
                <svg xmlns="http://www.w3.org/2000/svg" width="1" height="22" viewBox="0 0 1 22" fill="none" className="separator">
                  <path d="M0.5 0.5V21.5" stroke="white" stroke-opacity="0.5" stroke-linecap="square" />
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                  <path d="M12 4.7194H7.33333V0H4.66667V4.7194H0V7.4162H4.66667V12.1356H7.33333V7.4162H12V4.7194Z" fill="white" />
                </svg>
              </span>
            </div>
          </HeaderArea>
          {/* search row start */}

          <SearchRow className="search-content">
            <div className="left-side">
              <h6 className="heading-second">Content Score:</h6>
              <div className="engagement">
                <img src={Speed} alt="" />
                &nbsp;
                {totalArticleScore}
              </div>
            </div>
            <div className="search-bar-wrap">
              <div className="input-action">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" className="bar-left">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10.4733 9.57273L12.9094 12.0431C13.2324 12.3707 13.2324 12.9166 12.9228 13.2305C12.7613 13.3943 12.546 13.4762 12.3306 13.4762C12.1153 13.4762 11.9 13.3943 11.7385 13.2305L9.30239 10.7601C8.37373 11.4562 7.21627 11.8793 5.97805 11.8793C2.89596 11.8793 0.37915 9.3407 0.37915 6.20156C0.37915 3.07606 2.8825 0.523804 5.97805 0.523804C9.06013 0.523804 11.5769 3.06242 11.5769 6.20156C11.5769 7.47087 11.1597 8.63098 10.4733 9.57273ZM5.96459 2.21621C3.79771 2.21621 2.03459 4.00416 2.03459 6.20156C2.03459 8.41261 3.79771 10.2006 5.96459 10.1869C8.13147 10.1869 9.89458 8.39896 9.89458 6.20156C9.89458 4.00416 8.13147 2.21621 5.96459 2.21621Z"
                    fill="#424B50"
                  />
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="22" viewBox="0 0 2 22" fill="none">
                  <path d="M0.737817 0.880951V21.119" stroke="#DDDDDD" stroke-width="0.8" stroke-linecap="square" />
                </svg>
              </div>
              <div className="content-contact-searchbar">
                <SearchField
                  placeholder="Search contacts..."
                  handleSearch={(value: string) => {
                    setSearchedText(value.trim());
                  }}
                  reset={searchedText}
                ></SearchField>
              </div>
            </div>
          </SearchRow>

          {/* search row end */}

          <HeadingRow className="heading-row">
            <h6 className="heading-table">Total Contacts ({totalContentContacts})</h6>
          </HeadingRow>
          {contentContacts !== undefined ? (
            <>
              <TableSort
                columns={columnsData}
                data={contentContacts}
                module={'delivered'}
                _onCellClick={sortedResults}
                _onRowClick={RedirectToContactDetails}
                hasMore={(contentContactsLoading && contentContacts.length === 0) || contentContacts?.length < totalContentContacts}
                // hasMore={true}
                next={() => getContentContactsList(getParams(contentContacts.length))}
                loader={[1, 2, 3].map((item) => (
                  <ContactLoader key={item} loading={contentContactsLoading} />
                ))}
                useCustomQuery
              />
              {!contentContactsLoading && contentContacts?.length === 0 ? <div className="no-data">No Data Found!</div> : null}
            </>
          ) : (
            <div className="no-data">No Data Found!</div>
          )}
        </div>
      </Wrapper>
    </>
  );
};

export default ContentContact;

const Circle = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background: ${(props) => props.color};
  border-radius: 50%;
  margin-right: 3px;
`;

const Wrapper = styled.div`
  #scrollableDiv {
    .infinite-scroll-component {
      position: relative;
      min-height: auto;
    }
  }
  /* border-radius: 10px; */
  /* overflow: hidden; */
  .content-contact-searchbar {
    width: 200px;
    .search-wraper .search-bar {
      display: flex;
      align-items: center;
      height: 34px;
    }
    .search-wraper .search-bar span.refresh-search {
      margin: 0 8px;
    }
    span.clear-search {
      margin-top: 0 !important;
    }
    .search-wraper .search-bar .search-input {
      margin-top: 0;
    }
  }
  width: 100%;
  margin-bottom: 10px;
  /* padding: 14px 13px 14px 18px; */
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    /* border-radius: 7px; */
    /* padding: 10px 12px; */
    min-height: 83px;
    margin-top: 10px;
  }
  > div {
    width: 100%;
  }
  svg {
    rect.personality {
      x: 46%;
      width: 7%;
      @media (max-width: 1621px) {
        x: 33%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 38%;
        width: 9%;
      }
    }
    rect.company {
      x: 26%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 21%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 28%;
        width: 7%;
      }
    }
    rect.totalOpen {
      x: 63%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 46%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 50%;
        width: 3%;
      }
    }
    rect.uniqueOpen {
      x: 69%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 55%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 57%;
        width: 3%;
      }
    }
    rect.totalClick {
      x: 75%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 64%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 64%;
        width: 3%;
      }
    }
    rect.uniqueClick {
      x: 81%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 73%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 71%;
        width: 3%;
      }
    }

    rect.campaignScore {
      x: 87%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 81%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 78%;
        width: 8%;
      }
    }

    rect.lifetimeScore {
      x: 94%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 91%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 90%;
        width: 8%;
      }
    }
  }

  .no-data {
    position: absolute;
    bottom: 30%;
    width: 85%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'robotomedium';
    color: #67696a;
    font-size: 1rem;
    /* box-shadow: 6px 30px 73px -10px grey; */
    border-radius: 5px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      bottom: 28%;
    }
  }
  > div {
    width: 100%;
  }
  table {
    width: 100%;
    margin-top: 10px;
    margin-bottom: 20px;
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        th {
          color: #5e7079;
          font-weight: 700;
          font-size: 18px;
          font-family: 'Roboto', sans-serif;
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          padding-right: 0px;
        }
      }
    }
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        padding: 0 15px;
        &:before {
          max-height: 50px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            max-height: 38px;
          }
        }
      }
      th {
        color: #5e7079;
        font-weight: 700;
        font-size: 18px;
        font-family: 'Roboto', sans-serif;
        white-space: nowrap;
        span {
          width: 15px;
          display: flex;
          flex-direction: column;
          gap: 1.5px;
          .sort_asc {
            width: 10px;
            height: 8px;
            // margin-top: -15px;
            margin-left: 5px;
            float: left;
            // @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            //   margin-top: -13px;
            // }
            img {
              transform: rotate(180deg);
            }
          }
          .sort_desc {
            width: 10px;
            height: 8px;
            float: left;
            margin-left: 5px;
            // margin-top: 5px;
            img {
              float: left;
            }
          }
        }
      }
      tr:nth-child(1) {
        justify-content: end;
        display: flex;
        th {
          font-size: 18px;
          font-weight: 700;
          color: #5e7079;
          font-family: 'Roboto', sans-serif;
          justify-content: center;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(2) {
            width: 400px;
            @media all and (max-width: 1439px) {
              width: 300px;
            }
          }
          &:nth-child(3) {
            width: 200px;
            @media all and (max-width: 1439px) {
              width: 110px;
            }
          }
          &:nth-child(4) {
            width: 240px;
            @media all and (max-width: 1439px) {
              width: 200px;
            }
          }
        }
      }
      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: 'Roboto', sans-serif;
          white-space: nowrap;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(1) {
            padding-left: 55px;
          }
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      height: calc(100vh - 360px);
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: calc(100vh - 300px);
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      /* Handle on hover */
      /* &::-webkit-scrollbar-thumb:hover {
        background: #efefef;
      } */
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      float: left;
      tr:nth-child(1) {
        th {
          &:nth-child(1) {
            @media all and (max-width: 1439px) {
              //Safari (7.1+)
              _::-webkit-full-page-media,
              _:future,
              :root .safari_only {
                width: calc(100% - 535px) !important;
              }
              //Safari (from 6.1 to 10)
              @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
                @media {
                  width: calc(100% - 535px) !important;
                }
              }
              //Safari (10.1+)
              @media not all and (min-resolution: 0.001dpcm) {
                @media {
                  width: calc(100% - 535px) !important;
                }
              }
            }
          }
        }
      }
      tr {
        width: 100%;
        display: inline-flex;
        justify-content: space-between;
        td {
          min-height: 70px;
          font-family: 'Roboto', sans-serif;
          font-weight: 500;
          font-size: 20px !important;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px !important;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &[role='cell'] {
            color: #424b50;
          }
          &:nth-child(1) {
            width: calc(80% - 890px);
            display: flex;
            align-items: center;
            @media all and (max-width: 1621px) {
              width: calc(90% - 780px);
            }
            @media all and (max-width: 1439px) {
              width: calc(100% - 620px);
            }

            > div {
              display: flex;
              width: 100%;
              align-items: center;
              flex-direction: row;
              min-height: 65px;
              .preview {
                float: left;
              }
              .details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: 'Roboto Slab', serif;
                overflow: hidden;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
                width: 100%;
                .name {
                  font-weight: 400;
                  color: #000000;
                  font-size: 20px;
                  font-family: 'Roboto Slab', serif;
                  width: 100%;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    line-height: 24px;
                  }
                  // .ant-typography {
                  //   margin: 0;
                  //   max-width: 100%;
                  //   width: 100%;
                  // }
                }
                .company {
                  color: #1ec252;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: 'Roboto', sans-serif;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                  .ant-typography {
                    margin: 0;
                    // max-width: 100%;
                    color: #1ec252;
                    font-weight: 500;
                    font-size: 14px;
                  }
                }
                .designation {
                  color: #424b50;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: 'Roboto', sans-serif;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                  .ant-typography {
                    margin: 0;
                    // max-width: 100%;
                  }
                }
              }
            }
          }
          &:nth-child(2) {
            width: 20%;
            text-align: left;
            display: inline-flex;
            align-items: center;
            .company-name {
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
              word-break: break-word;
              color: #424b50;
              font-size: 16px;
              padding-left: 5px;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
              @media all and (max-width: 1125px) {
                padding-left: 12px;
              }
            }
            @media all and (max-width: 1621px) {
              width: 10%;
            }
            @media all and (max-width: 1439px) {
              width: 75px;
            }
          }
          &:nth-child(3) {
            width: 250px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1621px) {
              width: 140px;
            }
            @media all and (max-width: 1439px) {
              width: 81px;
            }
          }
          &:nth-child(4) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 50px;
            }
          }
          &:nth-child(5) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 50px;
            }
          }
          &:nth-child(6) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 50px;
            }
          }
          &:nth-child(7) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 50px;
            }
          }
          &:nth-child(8) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 100px;
            }
          }
          &:nth-child(9) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 90px;
            }
          }
        }
      }
    }
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: 'robotomedium';
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    white-space: nowrap;
    @media all and (max-width: 1439px) {
      font-size: 12px;
      padding: 4px 8px;
    }
  }
  .blog-image {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .blog-image {
    .img1 {
      height: 70px;
      width: 52px;
      border-radius: 6px 0px 0px 6px;
      margin-right: 2px;
      @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
        border-radius: 4px 0px 0px 4px;
        height: 50px;
        width: 36px;
      }
    }
    .img-bg {
      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;
    }
    .right-images {
      .overlayed-image {
        position: absolute;
        left: 9%;
        top: 22%;
      }
      .img-bg {
        background-position: center center;
        background-size: cover;
        background-repeat: no-repeat;
      }
      display: flex;
      flex-direction: column;
      .img2 {
        height: 34px;
        width: 39px;
        border-radius: 0px 6px 0px 0px;
        margin-bottom: 1px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 4px 0px 0px;
          height: 25px;
          width: 28px;
        }
      }
      .img3 {
        height: 34px;
        width: 39px;
        border-radius: 0px 0px 6px 0px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 0px 4px 0px;
          height: 23px;
          width: 28px;
        }
      }
    }
  }
  .disabled {
    pointer-events: none !important;
    background-color: rgba(225, 225, 225, 0.7) !important;
    cursor: no-drop;
  }
  .preview {
    position: relative;
    max-width: 55px;
    -ms-flex-negative: 1 !important;
    flex-shrink: 1 !important;
    img {
      border-radius: 100%;
      // max-width: 100%;
      height: 55px;
      width: 55px;
      min-width: 55px;
      outline: none;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: 40px;
        width: 40px;
        min-width: 40px;
      }
    }
  }
  .details {
    padding-left: 15px;
    width: 75%;
    .name,
    .company,
    .designation,
    .recipient-count {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0px;
      -webkit-box-align: start;
      width: 100%;
      word-break: break-word;
      .ant-typography-ellipsis-single-line {
        width: 300px;
        @media all and (max-width: 1439px) {
          width: 91px !important;
        }
      }
    }
    .name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 25px;
      font-family: 'Roboto Slab', serif;
      display: block;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 15px;
        font-size: 13px;
      }
      .ant-typography-ellipsis-single-line {
        width: 300px;
        @media all and (max-width: 1439px) {
          width: 100% !important;
        }
      }
    }
    .list-name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 22px;
      font-family: 'Roboto Slab', serif;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;

      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 14px;
        font-size: 13px;
      }
    }
    .company {
      -webkit-line-clamp: 1;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      color: #424b50;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
      .ant-typography {
        margin: 0;
        // max-width: 100%;
        color: #1ec252;
        font-weight: 500;
        font-size: 14px;
      }
    }
    .designation {
      -webkit-line-clamp: 1;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
    .recipient-count {
      -webkit-line-clamp: 1;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
  }
  .actions {
    -ms-flex-negative: 1;
    flex-shrink: 1;
    align-self: center;
    margin-left: auto;
    min-width: 106px;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      width: 100%;
      li {
        float: left;
        display: inline-block;
        padding: 0px;
        &.clicks {
          padding-right: 10px;
          .container-span {
            font-family: 'Roboto', sans-serif;
            color: #fff;
            font-size: 16px;
            font-weight: 500;
            background-color: #1971eb;
            border-radius: 12px;
            padding: 0px 10px;
            display: inline-flex;
            width: 80px;
            height: 22px;
            justify-content: center;
            align-items: center;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 11px;
              width: 70px;
            }
            svg {
              vertical-align: baseline;
              margin-right: 5px;
            }
          }
        }
        &.card-popover {
          a {
            padding: 0 5px;
            svg {
              width: 5px;
              max-height: none;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                width: 3px;
              }
            }
          }
        }
      }
    }
  }
`;
const HeadingRow = styled.div`
  margin-top: 27px;
  .heading-table {
    color: #424b50;
    font-family: 'Roboto Slab', serif;
    font-size: 23px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px; /* 78.261% */
    letter-spacing: -0.36px;
  }
`;

const HeaderArea = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  .left-side {
    .heading-l {
      color: #7a7b79;
      font-family: 'Roboto';
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.36px;
      margin-bottom: 0px;
      @media all and (max-width: 1280px) {
        width: 300px !important;
      }
    }
  }
  .right-side {
    display: flex;
    align-items: center;
    .action {
      color: #7e7e7e;
      font-family: 'Roboto';
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: -0.4px;
      padding: 6px 16px;
      border-radius: 8px;
      border: 1px solid #dadada;
      background: #fff;
      display: flex;
      align-items: center;
      margin-right: 16px;
      cursor: pointer;
      &:last-child {
        margin-right: 0px;
        padding: 5px 16px;
      }
    }
    .action-three {
      border-radius: 8px;
      border: 1px solid #1ec252;
      background: #1ec252;
      color: #fff;
    }
    span.action.action-two.disabled {
      background-color: rgba(225, 225, 225, 0.7) !important;
    }
    span.action.action-three.disabled {
      background-color: #1ec252 !important;
    }
    .separator {
      margin: 0px 8px 0px 9px;
    }
  }
`;

const SearchRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 14px;
  .heading-second {
    color: #001823;
    font-family: 'Roboto Slab', serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px; /* 100% */
    letter-spacing: -0.4px;
    margin-right: 8px;
    margin-bottom: 0px;
  }
  .left-side {
    display: flex;
    align-items: center;
  }
  .search-bar-wrap {
    position: relative;
    .input-action {
      display: flex;
      align-items: center;
      position: absolute;
      top: 50%;
      left: 15px;
      transform: translateY(-50%);
      .bar-left {
        margin-right: 8px;
      }
    }
    .input-wrap {
      padding: 9px 25px 9px 45px;
      border-radius: 21px;
      border: 1px solid #dadada;
      background: #fff;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding: 6px 25px 6px 45px;
      }
      &:focus,
      &:focus-visible {
        outline: none;
      }
      &::placeholder {
        color: #c1c1c1;
        font-family: 'Roboto';
        font-size: 14px;
        font-style: italic;
        font-weight: 400;
        line-height: 16px; /* 111.111% */
        letter-spacing: -0.36px;
      }
    }
  }
`;

const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
  .back-overlay {
    position: fixed;
    background: grey;
    opacity: 0.2;
    height: 100vh;
    width: 100vw;
    top: 0;
    left: 0;
    z-index: 16;
  }
  .setting-delete-dialog {
    width: 326px !important;
    .message {
      font-family: 'Roboto' !important;
      font-weight: 700 !important;
      line-height: 26px !important;
      font-size: 20px !important;
    }
  }
`;
